import { useState, useCallback, useRef, useEffect } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import qs from 'query-string';

import {
  accountUpdate as accountUpdateMutation,
  emailAuthenticationRequest as emailAuthenticationRequestMutation,
} from '../../../../graphql/mutations/account';
import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import { loginSuccess } from '../../../../actions/auth-actions';
import { openAlert } from '../../../../actions/modal-actions';
import { getErrorString } from '../../../../util/normalize-errors';
import { MeQuery } from '../../../../graphql/queries/account';
import { sentryCaptureMessage } from '../../../../util/sentry';
import { gtm } from '../../../../util/analytics';
import { getPhotoSearchCheckoutSearchItemsQuery } from '../../../temp/views/onboarding/PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import { formatPriceForDisplay } from '../../../../util/format-money';
import { ALBUM_SUBTYPE } from '../../../../constants/albums';
import useStoreReferralAttributes from '../../../../hooks/useStoreReferralAttributes';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';
import useGoogleRecaptcha from '../../../../services/recaptcha/useGoogleRecaptcha';
import usePhoneAuth from '../../../../components/auth/hooks/usePhoneAuth';
import { ContributorInvitationDetails } from '../../../../graphql/queries/contributors';
import { getContributorInvitationDetailsSearchQuery } from '../../../pro/views/InvitationResolver/helpers';
import { PURCHASE_TYPE } from '../../../../components/global/ThankYouView/constants';
import useOpenModal from '../../../../hooks/useOpenModal';
import { THANK_YOU_MODAL } from '../../../../components/global/ThankYouView';
import useEmailPinAuth from '../../../../components/auth/hooks/useEmailPinAuth';
import { SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD } from '../../../temp/views/onboarding/PhotoSearchCheckout/helpers';
import { isValidPhone } from '../../../../util/phone';
import {
  PINTEREST_CATEGORIES,
  PINTEREST_EVENTS,
} from '../../../../services/pinterest/constants';
import usePinterestTracking from '../../../../services/pinterest/hooks/usePinterestTracking';

export const REGISTRATION_STEPS = {
  PLAN: 'PLAN',
  EVENT_SKU_SELECTION: 'EVENT_SKU_SELECTION',
  DETAILS: 'DETAILS',
  PIN_VERIFY: 'PIN_VERIFY',
  EMAIL_PIN_VERIFY: 'EMAIL_PIN_VERIFY',
  VERIFICATION_EMAIL: 'VERIFICATION_EMAIL',
  CHECKOUT: 'CHECKOUT',
};

const CUSTOM_EVENT_KEY = 'CUSTOM_EVENT';

const useShutterbugRegistration = ({ isSignupAlbumsFlow } = {}) => {
  const openModal = useOpenModal();
  const { onTrackEvent: onTrackPinterestEvent } = usePinterestTracking();
  const { data: { searchSubscriptionItems = [] } = {} } = useQuery(
    getPhotoSearchCheckoutSearchItemsQuery,
  );
  const {
    data: { contributorInvitationDetails },
  } = useQuery(ContributorInvitationDetails);

  const { plan: queryPlan = null } = useParams();
  const location = useLocation();
  const history = useHistory();
  const {
    hasActiveReferralAttribute,
    accountReferralCreate,
    referralAttributes,
  } = useStoreReferralAttributes();
  const { eventType, paid } = qs.parse(location?.search);
  const isValidEventType =
    !isSignupAlbumsFlow &&
    // Using keys here because of custom events.
    (Object.values(ALBUM_SUBTYPE).includes(eventType) ||
      // Because custom events use NULL as sub-type, we need to
      // apply a custom key here.
      eventType === CUSTOM_EVENT_KEY);

  // Parse because of custom events:
  const albumSubtype = (() => {
    if (!isValidEventType) {
      return undefined;
    }

    if (eventType === CUSTOM_EVENT_KEY) {
      return null;
    }

    return eventType;
  })();

  const [step, setStep] = useState(
    isValidEventType
      ? REGISTRATION_STEPS.EVENT_SKU_SELECTION
      : REGISTRATION_STEPS.DETAILS,
  );
  const userId = useRef(null);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(queryPlan?.toUpperCase?.());
  const dispatch = useDispatch();
  const onErrorModalOpen = useCallback(state => dispatch(openAlert(state)), [
    dispatch,
  ]);
  const onLogin = useCallback(() => dispatch(loginSuccess()));
  const client = useApolloClient();
  const [emailAuthenticationRequest] = useMutation(
    emailAuthenticationRequestMutation,
  );
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const [checkout] = useMutation(checkoutMutation);
  const { onTokenRequest } = useGoogleRecaptcha();
  const {
    loading: phoneAuthLoading,
    pinSent,
    requireHumanInput,
    blockLogin,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  } = usePhoneAuth();
  const { onEmailAuthenticationRequest, onLoginByEmail } = useEmailPinAuth();

  useEffect(() => {
    if (pinSent && step !== REGISTRATION_STEPS.PIN_VERIFY) {
      setStep(REGISTRATION_STEPS.PIN_VERIFY);
    }
  }, [pinSent]);

  const onPhoneVerificationRequest = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const token = await onTokenRequest({ action: 'login' });
      await onPhoneAuthSubmit(
        values.phone_number,
        true,
        token,
        values.force_allow_action,
      );
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPinCodeResend = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const token = await onTokenRequest({ action: 'login' });
      await onResendPhoneAuthCode(
        values.phone_number,
        true,
        token,
        values.force_allow_action,
      );
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error sending the PIN.',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPinVerify = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      await onPhoneAuthVerify(values.phone_number, values.code);

      const {
        data: { me },
      } = await client.query({
        query: MeQuery,
        fetchPolicy: 'network-only',
      });

      userId.current = me.id;

      await accountUpdate({
        variables: {
          input: {
            id: me.id,
            name: `${values.first_name} ${values.last_name}`.trim(),
            email_address: values.email_address,
            state_id: values.state_id ?? undefined,
            client_store_items: [
              {
                key: 'shutterbug_signup_timestamp',
                value: moment().valueOf().toString(),
              },
            ],
          },
        },
      });

      if (!plan) {
        gtm.trackEvent({
          type: 'FreeRegistration',
          action: null,
        });

        await onLogin();

        if (hasActiveReferralAttribute) {
          accountReferralCreate(ACCOUNT_REFERRAL_TYPE.ACCOUNT_SIGNUP);
        }

        if (isSignupAlbumsFlow) {
          return;
        }

        if (contributorInvitationDetails) {
          history.push('/contributors/invres');

          return;
        }

        if (isValidEventType) {
          const { selectedEventAddonItemIds, eventItemId } = values;

          history.push(
            `/app/shutterbug/albums/create?${qs.stringify(
              {
                first_name: values.first_name,
                last_name: values.last_name,
                eventType: albumSubtype,
                selectedEventAddonItemIds,
                eventItemId,
              },
              { arrayFormat: 'bracket' },
            )}`,
          );
        } else {
          history.push('/app');
        }
      } else {
        setStep(REGISTRATION_STEPS.CHECKOUT);
      }
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onEmailPinVerify = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const {
        data: { accountLoginByEmail: accountLoginByEmailResponse } = {},
      } = await onLoginByEmail({
        emailAddress: values.email_address,
        code: values.code,
      });

      if (accountLoginByEmailResponse.success) {
        const {
          data: { me },
        } = await client.query({
          query: MeQuery,
          fetchPolicy: 'network-only',
        });

        userId.current = me.id;

        await accountUpdate({
          variables: {
            input: {
              id: me.id,
              name: `${values.first_name} ${values.last_name}`.trim(),
              contact_phone: isValidPhone(values.phone_number)
                ? values.phone_number
                : undefined,
              state_id: values.state_id ?? undefined,
              client_store_items: [
                {
                  key: 'shutterbug_signup_timestamp',
                  value: moment().valueOf().toString(),
                },
              ],
            },
          },
        });

        if (!plan) {
          gtm.trackEvent({
            type: 'FreeRegistration',
            action: null,
          });

          await onLogin();

          if (hasActiveReferralAttribute) {
            accountReferralCreate(ACCOUNT_REFERRAL_TYPE.ACCOUNT_SIGNUP);
          }

          if (isSignupAlbumsFlow) {
            return;
          }

          if (contributorInvitationDetails) {
            history.push('/contributors/invres');

            return;
          }

          if (isValidEventType) {
            const { selectedEventAddonItemIds, eventItemId } = values;

            history.push(
              `/app/shutterbug/albums/create?${qs.stringify(
                {
                  first_name: values.first_name,
                  last_name: values.last_name,
                  eventType: albumSubtype,
                  selectedEventAddonItemIds,
                  eventItemId,
                },
                { arrayFormat: 'bracket' },
              )}`,
            );
          } else {
            history.push('/app');
          }
        } else {
          setStep(REGISTRATION_STEPS.CHECKOUT);
        }
      } else {
        throw new Error('Could not verify user authenticity');
      }
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onSendEmailVerification = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);

      const { selectedEventAddonItemIds, eventItemId } = values;
      const { token, shareId } = contributorInvitationDetails ?? {};

      const nextUrl = isValidEventType
        ? `/app/shutterbug/albums/create?${qs.stringify(
            {
              first_name: values.first_name,
              last_name: values.last_name,
              eventType: albumSubtype,
              selectedEventAddonItemIds,
              eventItemId,
              ...(referralAttributes || {}),
            },
            { arrayFormat: 'bracket' },
          )}`
        : `/app&${qs.stringify({
            first_name: values.first_name,
            last_name: values.last_name,
            ...(referralAttributes || {}),
            ...(token && shareId
              ? getContributorInvitationDetailsSearchQuery(
                  contributorInvitationDetails,
                )
              : {}),
          })}`;

      await emailAuthenticationRequest({
        variables: {
          input: {
            email_address: values.email_address,
            intent: 'LOGIN',
            redirect_path: `/auth/password-setup?next=${nextUrl}`,
          },
        },
      });
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onEmailRegistrationRequest = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);

      await onEmailAuthenticationRequest({
        emailAddress: values.email_address,
      });

      setStep(REGISTRATION_STEPS.EMAIL_PIN_VERIFY);
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onCheckout = async values => {
    if (loading) {
      return;
    }

    const selectedSearchSubscriptionItem = searchSubscriptionItems?.find(
      item => {
        return item.id === values.plan_id;
      },
    );

    try {
      setLoading(true);
      const { data: { checkout: checkoutResponse } = {} } = await checkout({
        variables: {
          input: {
            account_id: userId.current,
            stripe_token: values.stripe_token.id,
            stripe_payment_method_id:
              values?.stripe_token?.stripe_payment_method_id,
            stripe_connect_account_id:
              values?.stripe_token?.stripe_connect_account_id,
            email_address: values.stripe_token?.email ?? values.email,
            search_subscription_items: [
              {
                search_subscription_item_id: values.plan_id,
              },
            ],
            platform: 'WEB',
          },
        },
      });

      // Track conversion, currently always on Free Trial during registration.
      if (selectedSearchSubscriptionItem) {
        if (selectedSearchSubscriptionItem.trial_days > 0) {
          gtm.trackCustomEvent({
            event: 'Plus_Trial_Purchased',
            consumer_subtype: 'CONSUMER',
            user_id: userId.current,
            price: 0,
          });
          onTrackPinterestEvent({
            event: PINTEREST_EVENTS.CHECKOUT,
            data: {
              order_id: checkoutResponse?.transaction?.id,
              currency: 'USD',
              content_name: 'Plus_Trial_Purchased',
              content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
              num_items: 1,
              value: 0,
            },
          });
        } else if (
          selectedSearchSubscriptionItem.billing_period ===
          SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.MONTHLY
        ) {
          gtm.trackCustomEvent({
            event: 'Plus_Monthly_Purchased',
            consumer_subtype: 'CONSUMER',
            user_id: userId.current,
            price: formatPriceForDisplay(checkoutResponse?.total_price?.value),
          });
          onTrackPinterestEvent({
            event: PINTEREST_EVENTS.CHECKOUT,
            data: {
              order_id: checkoutResponse?.transaction?.id,
              currency: 'USD',
              content_name: 'Plus_Monthly_Purchased',
              content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
              num_items: 1,
              value: formatPriceForDisplay(
                checkoutResponse?.total_price?.value,
              ),
            },
          });
        } else if (
          selectedSearchSubscriptionItem.billing_period ===
          SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.YEARLY
        ) {
          gtm.trackCustomEvent({
            event: 'Plus_Annual_Purchased',
            consumer_subtype: 'CONSUMER',
            user_id: userId.current,
            price: formatPriceForDisplay(checkoutResponse?.total_price?.value),
          });
          onTrackPinterestEvent({
            event: PINTEREST_EVENTS.CHECKOUT,
            data: {
              order_id: checkoutResponse?.transaction?.id,
              currency: 'USD',
              content_name: 'Plus_Annual_Purchased',
              content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
              num_items: 1,
              value: formatPriceForDisplay(
                checkoutResponse?.total_price?.value,
              ),
            },
          });
        }

        const purchaseMetadata = {
          subscriptionDetails: {
            searchSubscriptionItem: selectedSearchSubscriptionItem,
            freeTrialAvailable: selectedSearchSubscriptionItem.trial_days > 0,
          },
          verticalDetails: {
            persona: undefined,
            basename: 'consumer',
          },
        };

        openModal(THANK_YOU_MODAL, {
          purchaseType: PURCHASE_TYPE.SEARCH_SUBSCRIPTION,
          purchaseMetadata,
        });
      }

      // Legacy -- Should be removed after 5/15/2022
      gtm.trackEvent({
        type: 'FreeTrialRegistration',
        action: plan,
      });

      // Forces log in and redirect to /app
      await onLogin();

      history.push('/app');
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
      sentryCaptureMessage('SHUTTERBUG SIGNUP - Registration Error - CC', {
        level: 'error',
        extra: {
          error: responseError,
        },
      });
      setLoading(false);
    }
  };

  const onEventSKUSelected = () => {
    setStep(REGISTRATION_STEPS.DETAILS);
  };

  return {
    plan,
    setPlan,
    step,
    setStep,
    onPhoneVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onSendEmailVerification,
    onEmailRegistrationRequest,
    onCheckout,
    loading: loading || phoneAuthLoading,
    eventType: albumSubtype,
    onEventSKUSelected,
    paidPlansOnly: paid,
    requireHumanInput,
    blockLogin,
    onEmailAuthenticationRequest,
    onEmailPinVerify,
  };
};

export default useShutterbugRegistration;
